import React, { useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../logo.svg';

export default function LoginPage() {
  const { user, login, requestCode, verifyCode } = useAuth();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | codeSent | error
  const [errorMsg, setErrorMsg] = useState('');
  const [devLink, setDevLink] = useState('');
  const [devCode, setDevCode] = useState('');
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [searchParams] = useSearchParams();

  // If already logged in, redirect to home (dashboard)
  if (user) return <Navigate to="/" replace />;

  const urlError = searchParams.get('error');
  const errorMessages = {
    missing_token: 'Invalid login link.',
    invalid_token: 'This login link is invalid or has expired.',
    token_used: 'This login link has already been used.',
    token_expired: 'This login link has expired. Please request a new one.',
    user_not_found: 'Account not found.',
    server_error: 'Something went wrong. Please try again.',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await login(email.trim());
      const data = await res.json();

      if (res.ok) {
        setStatus('sent');
        if (data.magicLink) setDevLink(data.magicLink);
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Something went wrong');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  const handleRequestCode = async () => {
    if (!email.trim()) {
      setStatus('error');
      setErrorMsg('Please enter your email first.');
      return;
    }
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await requestCode(email.trim());
      const data = await res.json();
      if (res.ok) {
        setStatus('codeSent');
        if (data.devCode) setDevCode(data.devCode);
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Something went wrong');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setErrorMsg('');
    try {
      const res = await verifyCode(email.trim(), code.trim());
      if (res.ok) {
        // AuthContext refreshes the session; redirect happens via <Navigate>
        return;
      }
      const data = await res.json();
      setErrorMsg(data.error || 'Invalid code');
    } catch {
      setErrorMsg('Network error. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="page login-page">
      <div className="login-card">
        <img src={logo} alt="Ridge Sports" className="login-logo" />
        <h1>Ridge Sports</h1>
        <p className="login-subtitle">Sign in with your email</p>

        {urlError && (
          <div className="alert alert-error">
            {errorMessages[urlError] || 'An error occurred.'}
          </div>
        )}

        {status === 'sent' ? (
          <div className="alert alert-success">
            <h3>Check your email!</h3>
            <p>We sent a magic sign-in link to <strong>{email}</strong>. It expires in 30 minutes.</p>
            {devLink && (
              <p style={{ marginTop: '12px' }}>
                <strong>DEV MODE:</strong>{' '}
                <a href={devLink}>Click here to sign in</a>
              </p>
            )}
          </div>
        ) : status === 'codeSent' ? (
          <>
            <div className="alert alert-success">
              <h3>Enter your code</h3>
              <p>We sent a 6-digit login code to <strong>{email}</strong>. It expires in 15 minutes.</p>
              {devCode && (
                <p style={{ marginTop: '12px' }}>
                  <strong>DEV MODE:</strong> Your code is <strong>{devCode}</strong>
                </p>
              )}
            </div>
            <form onSubmit={handleVerifyCode}>
              <div className="form-group">
                <label htmlFor="code">6-Digit Code</label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  autoFocus
                  required
                />
              </div>
              {errorMsg && <div className="alert alert-error">{errorMsg}</div>}
              <button type="submit" className="btn btn-primary btn-full" disabled={verifying || code.length !== 6}>
                {verifying ? 'Verifying...' : 'Verify Code'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-full"
                style={{ marginTop: '8px' }}
                onClick={() => { setStatus('idle'); setCode(''); setDevCode(''); setErrorMsg(''); }}
              >
                Start Over
              </button>
            </form>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
                disabled={status === 'sending'}
              />
            </div>

            {status === 'error' && (
              <div className="alert alert-error">{errorMsg}</div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Sending...' : 'Send Magic Link'}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-full"
              style={{ marginTop: '8px' }}
              onClick={handleRequestCode}
              disabled={status === 'sending'}
            >
              Request Login Code
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
